import React, { useEffect, useCallback, useState } from 'react';

import { Control, Margin } from '@vlabs/uikit';
import { get } from 'lodash';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type TFieldNames = string[];
type TReactComponentWithFieldNames = React.FC & { fieldNames: TFieldNames };

interface UseStepperFormProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  steps: TReactComponentWithFieldNames[];
  onSubmit: (values: T) => void;
  canGoNextStep?: () => boolean;
  buttonsClass?: string;
}

interface TStepperContext {
  isLastStep: boolean;
  isFirstStep: boolean;
  activeStep: number;
  nextStep: VoidFunction;
  prevStep: VoidFunction;
  goToStep: (stepNum: number) => void;
}

interface UseStepperFormReturn {
  controls: (props: TStepperContext) => React.ReactNode;
  activeStep: number;
}

export const useSteppedForm = <T extends FieldValues>({
  form,
  canGoNextStep = () => true,
  steps,
  onSubmit,
  buttonsClass = undefined,
}: UseStepperFormProps<T>): UseStepperFormReturn => {
  const [activeStep, setActiveStep] = useState(0);
  const { t } = useTranslation();

  const { trigger: validateForm, formState: { errors: formErrors }, getValues } = form;
  // FIXME: надо придумать что-то получше, используется чтобы если открыт календарь форма не переключалась на следующий шаг
  const isCalendarOrSelectMenuOpen = useCallback(() => (document.querySelector('.flatpickr-calendar.open') ?? document.querySelector('.Select__menu')), []);

  const controls = ({
    prevStep,
    nextStep,
    isLastStep,
    isFirstStep,
  }: TStepperContext) => {
    const onNextStep = async () => {
      await validateForm();

      const firstErrorField = steps[activeStep]?.fieldNames
        .find((fieldName: string) => get(formErrors, fieldName));

      if (!firstErrorField) {
        setActiveStep(activeStep + 1);
        nextStep();
      }
    };

    const onPrevStep = () => {
      setActiveStep(activeStep - 1);
      prevStep();
    };

    return (
      <div className={buttonsClass}>
        <Margin right>
          <Control.Button
            data-testid="stepper.prevStepButton"
            disabled={isFirstStep}
            onClick={onPrevStep}
            variant="outlined"
          >
            {t('handlers:форма.назад')}
          </Control.Button>
        </Margin>

        {!isLastStep && (
          <Control.Button
            data-testid="stepper.nextStepButton"
            onClick={onNextStep}
          >
            {t('handlers:форма.далее')}
          </Control.Button>
        )}

        {!!isLastStep && (
          <Control.Button
            data-testid="stepper.submitButton"
            type="submit"
          >
            {t('форма.submit')}
          </Control.Button>
        )}
      </div>
    );
  };

  useEffect(() => {
    async function keyDown(e: KeyboardEvent) {
      if (!canGoNextStep()) return;
      if (isCalendarOrSelectMenuOpen()) return;

      // FIXME: выглядит как очень грязный хак, надо придумать что-то получше
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();

        await validateForm();

        const firstErrorField = steps[activeStep]?.fieldNames
          .find((fieldName) => get(formErrors, fieldName));

        if ((activeStep === steps.length - 1) && (firstErrorField === undefined)) {
          onSubmit(getValues());
          return;
        }
        if (firstErrorField === undefined) {
          setActiveStep(activeStep + 1);
        }
      }
    }
    document.removeEventListener('keydown', keyDown, true);
    document.addEventListener('keydown', keyDown, true);
    return () => {
      document.removeEventListener('keydown', keyDown, true);
    };
  }, [validateForm,
    formErrors,
    canGoNextStep,
    activeStep,
    onSubmit,
    getValues,
    steps,
    isCalendarOrSelectMenuOpen]);

  return {
    controls,
    activeStep,
  };
};
